import React, { useState, useEffect, useRef } from 'react';
import { useParams ,useNavigate,useLocation  } from 'react-router-dom';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import { ref as dbRef, onValue, push, update } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase-config'; // Adjust path if needed
import './Wallet.css';

/**
 * WalletPaymentReceipt
 * A child component for the printable PDF receipt.
 * Now styled similarly to the Billing "PrintableReceipt" component.
 */
const WalletPaymentReceipt = React.forwardRef(({
  fullName,
  email,
  paymentId,
  orderId,
  paymentTimestamp,
  amountPaid,
  walletBalanceAfterPayment,
  currency = 'INR' // default to INR if not provided
}, ref) => {

  // Convert numeric timestamp to readable date-time
  let paymentDateString = "";
  if (paymentTimestamp) {
    const paymentDateObj = new Date(paymentTimestamp);
    paymentDateString =
      paymentDateObj.toLocaleDateString('en-GB') +
      ', ' +
      paymentDateObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
  }

  /**
   * Inline styles, mimicking the style approach in your Billing PrintableReceipt
   */
  const headerContainerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '8px solid transparent',
    backgroundSize: '100% 8px',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'bottom',
    marginBottom: '0px',
    marginLeft: '15px',
    marginRight: '15px'
  };

  const logoAndNameContainerStyles = {
    display: 'flex',
    alignItems: 'center',
  };

  const logoStyles = {
    height: 'auto',
    width: '48px',
  };

  const companyNameStyles = {
    color: '#436850',
    fontWeight: 'bold',
    fontSize: '60px',
    marginLeft: '10px',
  };

  const taglineStyles = {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#000',
    marginTop: '5px',
  };

  const contactDetailsStyles = {
    textAlign: 'right',
    color: '#000',
    fontSize: '16px',
  };

  const boldDetailStyles = {
    fontWeight: 'bold',
  };

  // Outer container for the “body” sections
  const usageSectionStyles = {
    margin: '0 80px',
    paddingBottom: '10px',
  };

  const transactionDetailContainerStyles = {
    marginTop: '20px',
    padding: '10px',
    backgroundColor: '#ffffff',
    boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
    marginBottom: '40px',
  };

  const transactionDetailHeadingStyles = {
    marginBottom: '20px',
    color: '#333',
    borderBottom: '2px solid #436850',
    paddingBottom: '5px',
    fontSize: '22px',
    fontWeight: 'bold',
  };

  const transactionDetailItemStyles = {
    display: 'flex',
    justifyContent: 'flex-start',
  };

  const transactionDetailLabelStyles = {
    display: 'block',
    color: '#555',
    marginBottom: '2px',
  };

  const transactionDetailValueStyles = {
    color: '#000',
    fontWeight: 'bold',
    marginBottom: '10px',
    fontSize: '16px',
  };

  // “GST Details” style container
  const gstSectionStyles = {
    padding: '10px',
    backgroundColor: '#ffffff',
    boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
    marginBottom: '10px',
  };

  const gstDetailStyles = {
    marginBottom: '3px',
    fontSize: '16px',
  };

  const gstDetailTableStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '6px',
  };

  const gstDetailColumnStyles = {
    flex: '1',
    padding: '0 10px',
  };

  // Table for “Description” vs “Value”
  const tableContainerStyles = {
    marginTop: '10px',
    padding: '10px',
    backgroundColor: '#ffffff',
    boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
    marginBottom: '15px',
  };

  const tableStyles = {
    width: '100%',
    borderCollapse: 'collapse',
  };

  const thStyles = {
    textAlign: 'left',
    padding: '10px',
    border: '1px solid #436850',
    fontSize: '18px',
    fontWeight: 'bold',
  };

  const tdStyles = {
    padding: '10px',
    border: '1px solid #ddd',
    fontSize: '16px',
  };

  return (
    <div id="printableWalletReceipt" className="receipt-container" ref={ref} style={{ fontFamily: 'Arial, sans-serif' }}>

      {/* ---- HEADER SECTION ---- */}
      <div style={headerContainerStyles}>
        <div>
          <div style={logoAndNameContainerStyles}>
            <img src="/easiofy.png" alt="Easiofy Logo" style={logoStyles} />
            <span style={companyNameStyles}>EASIOFY</span>
          </div>
          <div style={taglineStyles}>Easiofy Solutions Pvt Ltd</div>
        </div>
        <div style={contactDetailsStyles}>
          <div style={boldDetailStyles}>

            I 1607, AVJ heights, sector <br /> zeta1, Greater Noida 201307
            <i className="fas fa-map-marker-alt" style={{ marginLeft: '5px' }}></i>
          </div>
          <div>

            +91 99584 99337
            <i className="fas fa-phone" style={{ marginLeft: '5px' }}></i>
          </div>
          <div>
            noor.fatma@easiofy.com
            <i className="fas fa-envelope" style={{ marginLeft: '5px' }}></i>

          </div>
          <div>

            <a href="https://easiofy.com/" style={{ textDecoration: 'none', color: '#333' }}>
              https://easiofy.com/
            </a>
            <i className="fas fa-globe" style={{ marginLeft: '5px' }}></i>
          </div>
        </div>
      </div>

      <div
        style={{
          width: '96%',
          height: '8px',
          background: 'linear-gradient(to right, #436850 50%, #000 50%)',
          margin: '-20px auto 20px', // top: -20px, left/right: auto, bottom: 10px
        }}
      ></div>



      {/* ---- MAIN CONTENT SECTION ---- */}
      <div style={usageSectionStyles}>


        {/* Title */}
        <h2 style={{ textAlign: 'center', fontSize: '30px', fontWeight: 'bold', marginBottom: '10px', textTransform: 'uppercase' }}>
          Wallet Top-Up Receipt
        </h2>

        {/* GST Details Box (optional, but for consistency we show “From” / “Bill to”) */}
        <div style={gstSectionStyles}>
          <h5 style={transactionDetailHeadingStyles}>GST Details</h5>
          <div style={gstDetailTableStyles}>
            <div style={gstDetailColumnStyles}>
              <strong>From:</strong>
              <div style={gstDetailStyles}>Easiofy Solutions Pvt Ltd.</div>
              <div style={gstDetailStyles}>
                <strong>GSTIN:</strong> 09AAHCE2212N1ZK
              </div>
              <div style={gstDetailStyles}>
                <strong>Company's Address:</strong> I 1607, AVJ Heights, Sector Zeta1, Greater Noida - 201307.
              </div>
            </div>
            <div style={gstDetailColumnStyles}>
              <strong>To:</strong>
              <div style={gstDetailStyles}>
                {fullName || 'Not Available'}
              </div>
              <div style={gstDetailStyles}>
                <strong>Email:</strong> {email || 'Not Available'}
              </div>
              {/* If you have the user’s address or GSTIN for the wallet user, show them here.
                  Otherwise, you can show placeholders or remove them. */}
              {/* <div style={gstDetailStyles}>
                <strong>Client's Address:</strong> -
              </div> */}
            </div>
          </div>
        </div>

        {/* Transaction Details Box */}
        <div style={transactionDetailContainerStyles}>
          <h3 style={transactionDetailHeadingStyles}>Transaction Details</h3>
          <div>
            <div style={transactionDetailItemStyles}>
              <span style={transactionDetailLabelStyles}>Payment ID:</span>
              <span style={transactionDetailValueStyles}>{paymentId}</span>
            </div>
            <div style={transactionDetailItemStyles}>
              <span style={transactionDetailLabelStyles}>Order ID:</span>
              <span style={transactionDetailValueStyles}>{orderId}</span>
            </div>
            <div style={transactionDetailItemStyles}>
              <span style={transactionDetailLabelStyles}>Date &amp; Time:</span>
              <span style={transactionDetailValueStyles}>{paymentDateString}</span>
            </div>
          </div>
        </div>

        {/* Table Section (showing “Amount Added” and “Wallet Balance After Payment”) */}
        <div style={tableContainerStyles}>
          <table style={tableStyles}>
            <thead>
              <tr>
                <th style={thStyles}>Description</th>
                <th style={thStyles}>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyles}>Amount Added</td>
                <td style={tdStyles}>{currency === 'USD' ? '$' : '₹'}{amountPaid}</td>
              </tr>
              <tr>
                <td style={tdStyles}>Updated Balance</td>
                <td style={tdStyles}>{currency === 'USD' ? '$' : '₹'}{walletBalanceAfterPayment}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* Add this bold thank-you line */}
        <div
          style={{
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '18px',
            marginTop: '30px'
          }}
        >
          Thank you for your payment!
        </div>
      </div>
    </div>
  );
});


/**
 * Main Wallet component
 * (Unchanged logic, just updated the receipt layout to match your Billing style)
 */
const Wallet = ({ adminId: propAdminId, redirectRoute: propRedirectRoute }) => {
  const { id: paramAdminId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine the admin ID based on prop or URL param
  const id = propAdminId || paramAdminId;

// Determine final redirect route: check location.state, then prop, then default to '/Admin'
const finalRedirectRoute = location.state?.redirectRoute || propRedirectRoute || '/Admin';

  // Admin info
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [contact, setContact] = useState('');

  // Wallet info
  const [walletBalance, setWalletBalance] = useState(0);
  const [amountToAdd, setAmountToAdd] = useState('');
  const [currency, setCurrency] = useState('INR'); // Default to INR

  // Payment states
  const [orderId, setOrderId] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [paymentTimestamp, setPaymentTimestamp] = useState(null);

  // Loading / Payment Processing
  const [isLoading, setIsLoading] = useState(true);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);

  // PDF generation states
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showCheckmark, setShowCheckmark] = useState(false);

  const [fallingMoney, setFallingMoney] = useState(null);


  // Ref for the receipt
  const receiptRef = useRef(null);

  /**
   * FETCH ADMIN INFO + WALLET BALANCE
   */
  useEffect(() => {
    // 1) Fetch admin data
    const adminRef = dbRef(db, `superadmin/admins/${id}`);
    onValue(adminRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setFullName(data.FullName || '');
        setEmail(data.Email || '');
        setContact((data.Contact || '').replace(/\s+/g, ''));
        // Fetch currency
        setCurrency(data.Currency || 'INR');
      }
    });

    // 2) Fetch wallet balance
    const walletRef = dbRef(db, `superadmin/admins/${id}/Wallet/Wallet_Balance`);
    onValue(walletRef, (snapshot) => {
      if (snapshot.exists()) {
        setWalletBalance(snapshot.val());
      } else {
        setWalletBalance(0);
      }
      setIsLoading(false);
    });
  }, [id]);

  /**
   * RAZORPAY SCRIPT LOADER
   */
  const loadRazorpayScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  /**
   * HANDLE PAYMENT
   */
  const handleAddMoney = async () => {
    if (!amountToAdd || isNaN(amountToAdd) || Number(amountToAdd) <= 0) {
      alert('Please enter a valid amount.');
      return;
    }
    setIsPaymentProcessing(true);

    const scriptLoaded = await loadRazorpayScript('https://checkout.razorpay.com/v1/checkout.js');
    if (!scriptLoaded) {
      alert('Failed to load Razorpay. Check your connection.');
      setIsPaymentProcessing(false);
      return;
    }

    try {
      const amountInPaise = Number(amountToAdd) * 100;
      const orderResponse = await axios.post(
        'https://hqkcxtqdsi.execute-api.us-east-1.amazonaws.com/dev/create-order',
        { amount: amountInPaise, Currency: currency }
      );
      const dummyContact = '9999999999'; // fulfils Razorpay's 10‑digit requirement
      const adminContact = contact && contact.length >= 10 ? contact : dummyContact;
      console.log('Admin Contact:', adminContact);

      const orderData = orderResponse.data;
      const options = {
        key: 'rzp_test_0TrVVCr438tsgd',
        amount: orderData.amount,
        currency: currency,
        name: 'Easiofy Solutions Pvt Ltd',
        description: `Wallet Top-up for Admin: ${id}`,
        image: `${window.location.origin}/easiofy.png`,
        order_id: orderData.id,
        prefill: {
          name: fullName,
          email: email,
          contact: dummyContact
        },
        theme: {
          color: '#3399cc',
        },
        remember_customer: false,
        // method: undefined,
        handler: async (response) => {
          setIsPaymentProcessing(false);
          const verificationResponse = await axios.post(
            'https://hqkcxtqdsi.execute-api.us-east-1.amazonaws.com/dev/verify-payment',
            {
              orderCreationId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            }
          );

          if (verificationResponse.data.valid) {
            alert('Payment verified successfully.');
            setOrderId(orderData.id);
            setPaymentId(response.razorpay_payment_id);
            setPaymentTimestamp(Date.now());
          } else {
            alert('Payment verification failed.');
          }
        },
        modal: {
          ondismiss: () => {
            setIsPaymentProcessing(false);
          },
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error('Error creating Razorpay order:', error);
      alert('Error creating order. Please try again.');
      setIsPaymentProcessing(false);
    }
  };

  /**
   * Once orderId, paymentId, and paymentTimestamp are set => generate PDF
   */
  useEffect(() => {
    if (orderId && paymentId && paymentTimestamp) {
      generatePDFAndSave();
    }
    // eslint-disable-next-line
  }, [orderId, paymentId, paymentTimestamp]);

  const generatePDFAndSave = async () => {
    setIsGeneratingPdf(true);
    try {
      const element = document.getElementById('walletPaymentReceiptToPrint');
      const options = {
        margin: [0, 0, 2, 0],
        filename: `WalletReceipt-${orderId}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };

      // Generate PDF as a blob
      const pdfBlob = await html2pdf().set(options).from(element).toPdf().output('blob');

      // 1) Upload PDF to Firebase Storage
      const pdfStoragePath = `superadmin/admins/${id}/Wallet/Wallet_Payment_Receipt/Receipt-${orderId}.pdf`;
      const storageReference = storageRef(storage, pdfStoragePath);
      await uploadBytes(storageReference, pdfBlob);

      // 2) Get the download URL
      const pdfDownloadUrl = await getDownloadURL(storageReference);

      // 3) Update the Wallet_Balance
      const newBalance = walletBalance + Number(amountToAdd);
      const updates = {};
      updates[`superadmin/admins/${id}/Wallet/Wallet_Balance`] = newBalance;

      // 4) Push an entry to "Wallet_Payment_Receipt"
      const walletReceiptsRef = dbRef(db, `superadmin/admins/${id}/Wallet/Wallet_Payment_Receipt`);
      await update(dbRef(db), updates);
      await push(walletReceiptsRef, {
        orderId,
        paymentId,
        paymentTimestamp: formatFirebaseTimestamp(paymentTimestamp),
        pdfDownloadUrl,
        amountAdded: Number(amountToAdd),
        walletBalanceAfterPayment: newBalance,
      });

      setShowCheckmark(true);
      setAmountToAdd('');
      // Calculate the new balance and trigger the falling animation
const UpdatedBalance = walletBalance + Number(amountToAdd);

// Trigger falling animation with the added amount
setFallingMoney(Number(amountToAdd));

// Then update the balance as you already do:
setWalletBalance(UpdatedBalance);

      // Redirect after a short delay
      setTimeout(() => {
        navigate(finalRedirectRoute);
      }, 1000);
    } catch (error) {
      console.error('Error generating/uploading PDF for wallet top-up:', error);
      alert('Something went wrong generating receipt.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Format timestamps for saving
  const formatFirebaseTimestamp = (timestamp) => {
    const dateObj = new Date(timestamp);
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    const timeString = dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return `${day}/${month}/${year}, ${timeString}`;
  };

  // Loading overlay
  if (isLoading) {
    return (
      <div className="wallet-loader-overlay">
        <div className="wallet-loader-spinner"></div>
        <p>Loading Wallet Details...</p>
      </div>
    );
  }

  // PDF generation overlay
  if (isGeneratingPdf) {
    return (
      <div className="wallet-payment-spinner-overlay">
        {!showCheckmark ? (
          <div className="wallet-payment-spinner">
            <div className="lds-ripple"><div></div><div></div></div>
          </div>
        ) : (
          <div className="wallet-payment-checkmark">&#10003;</div>
        )}
        <div className="wallet-payment-message">
          <h2>{!showCheckmark ? 'Generating Wallet Receipt...' : 'Receipt Ready!'}</h2>
          <h5>You'll be redirected shortly.</h5>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-main-container">
      {/* <h1 style={{ marginTop: "4%" }}>Wallet : <strong>{id}</strong></h1> */}
      <h1 style={{ marginTop: "4%" }}><strong>{id}</strong></h1>
      <div style={{ position: 'relative', margin: '20px 0' }}>

      <p style={{ textAlign: 'center' }}>
            Current Balance: <strong>{currency === 'USD' ? '$' : '₹'}{walletBalance}</strong>
        </p>  {fallingMoney && (
    <div 
      className="falling-money" 
      onAnimationEnd={() => setFallingMoney(null)}
    >
      +{currency === 'USD' ? '$' : '₹'}{fallingMoney}
    </div>
  )}
</div>

      <div className="wallet-top-up-section">
        <label htmlFor="amountToAdd">Enter Amount ({currency === 'USD' ? '$' : '₹'}):</label>
        <input
          id="amountToAdd"
          type="number"
          value={amountToAdd}
          onChange={(e) => setAmountToAdd(e.target.value)}
          placeholder="100"
        />
        <button
          onClick={handleAddMoney}
          disabled={isPaymentProcessing}
          className="wallet-top-up-button"
        >
          {isPaymentProcessing ? 'Processing...' : 'ADD'}
        
        </button>
      </div>

      {/* Hidden receipt container for PDF generation */}
      <div style={{ display: 'none' }}>
        <div id="walletPaymentReceiptToPrint">
          <WalletPaymentReceipt
            ref={receiptRef}
            adminId={id}
            fullName={fullName}
            email={email}
            paymentId={paymentId}
            orderId={orderId}
            paymentTimestamp={paymentTimestamp}
            amountPaid={Number(amountToAdd)}
            walletBalanceAfterPayment={walletBalance + Number(amountToAdd)}
            currency={currency}
          />
        </div>
      </div>
    </div>
  );
};

export default Wallet;
